import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { Plus, Minus, AlertTriangle, Play, Timer } from "lucide-react";
import { CardHeader, EmptyState, GlassCard, PersonAvatar, T, TYPE, formatDate, labelSx, primaryButtonSx } from "../../user/complaints/shared";
import { useSocketEvent } from "../../helpdesk/socket";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { TypeIcon, PriorityIcon, Assignee } from "../ui";
import { fieldSx } from "../inputs";
import { SprintFormDialog } from "./SprintDialogs";
import { UNIT_LABEL } from "./BoardBacklog";

const IssueLine = ({ issue, action, unit }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.6, px: 1, borderBottom: `1px solid ${T.border}` }}>
    <TypeIcon typeId={issue.typeId} size={13} />
    <Typography sx={{ fontSize: TYPE.micro, fontWeight: 700, minWidth: 64 }}>{issue.key}</Typography>
    <Typography noWrap sx={{ fontSize: TYPE.small, flexGrow: 1 }}>{issue.summary}</Typography>
    {issue.estimate > 0 && <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{issue.estimate} {unit}</Typography>}
    <PriorityIcon priorityId={issue.priorityId} size={14} />
    <Assignee person={issue.assignee} size={20} />
    {action}
  </Stack>
);

/** Plan one sprint: pick issues from the backlog against the team's capacity. */
const SprintPlanning = ({ board, reloadBoard }) => {
  const ctx = usePm();
  const [sprints, setSprints] = useState(null);
  const [sprintId, setSprintId] = useState("");
  const [backlog, setBacklog] = useState(null);
  const [plan, setPlan] = useState(null);
  const [cap, setCap] = useState({});
  const [text, setText] = useState("");
  const [starting, setStarting] = useState(null);
  const [error, setError] = useState("");
  const project = board.project;
  const perms = board.permissions || [];
  const editable = perms.includes("sprint.update") && !project.archivedAt;
  const unit = UNIT_LABEL[board.estimationConfig?.unit || "storyPoints"];

  const load = useCallback(async () => {
    try {
      const list = await pm.boardSprints(board._id, { status: "future,active" });
      setSprints(list);
      const sid = sprintId && list.some((s) => s._id === sprintId) ? sprintId : list.find((s) => s.status === "future")?._id || list[0]?._id || "";
      setSprintId(sid);
      const b = await pm.boardBacklog(board._id, { text: text || undefined });
      setBacklog(b);
      if (sid) {
        const p = await pm.sprintPlanning(sid);
        setPlan(p);
        setCap(Object.fromEntries(p.people.map((x) => [String(x.memberId), { hours: x.capacityHours, points: x.capacityPoints, name: x.name }])));
      } else setPlan(null);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board._id, sprintId, text]);
  useEffect(() => {
    const t = setTimeout(load, text ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, text]);
  useSocketEvent(["issue.updated", "board.updated", "sprint.updated", "sprint.started"], () => load());

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!sprints || !backlog) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  if (!sprints.length) return <GlassCard><EmptyState icon={Timer} title="No sprint to plan" description="Create a sprint in the backlog first." /></GlassCard>;
  const sprint = sprints.find((s) => s._id === sprintId);
  const inSprint = backlog.sections.find((s) => s.key === sprintId);
  const pool = backlog.sections.find((s) => s.key === "backlog");
  const add = async (ids, to) => {
    try {
      await pm.rankOnBoard(board._id, { issueIds: ids, sprintId: to });
      load();
    } catch (err) {
      ctx.fail(err);
    }
  };
  const saveCap = async () => {
    try {
      await pm.updateSprint(sprintId, { capacity: Object.entries(cap).map(([memberId, c]) => ({ memberId, name: c.name, hours: Number(c.hours) || 0, points: Number(c.points) || 0 })) });
      ctx.notify("Capacity saved");
      load();
    } catch (err) {
      ctx.fail(err);
    }
  };
  const capHours = Object.values(cap).reduce((s, c) => s + (Number(c.hours) || 0), 0);
  const capPoints = Object.values(cap).reduce((s, c) => s + (Number(c.points) || 0), 0);
  const overHours = plan && capHours && plan.committed.hours > capHours;
  const overPoints = plan && capPoints && plan.committed.points > capPoints;
  const enforce = board.settings?.sprintStart?.enforceCapacity;

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <TextField select size="small" label="Sprint" value={sprintId} onChange={(e) => setSprintId(e.target.value)} sx={{ ...fieldSx, minWidth: 220 }}>
          {sprints.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}{s.status === "active" ? " (active)" : ""}</MenuItem>)}
        </TextField>
        {sprint?.goal && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>Goal: {sprint.goal}</Typography>}
        {sprint?.startDate && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatDate(sprint.startDate)} – {formatDate(sprint.endDate)}</Typography>}
        <Box sx={{ flexGrow: 1 }} />
        {sprint?.status === "future" && perms.includes("sprint.start") && ctx.me.isAdminRole && !project.archivedAt && <Button variant="contained" sx={primaryButtonSx} startIcon={<Play size={14} />} onClick={() => setStarting(sprint)}>Start sprint</Button>}
      </Stack>
      {plan && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
          {[["Issues", plan.committed.issues], [`Planned (${unit})`, board.estimationConfig?.unit === "storyPoints" || !board.estimationConfig?.unit ? plan.committed.points : inSprint?.estimate ?? 0], ["Planned hours", plan.committed.hours], ["Team capacity", `${capHours}h${capPoints ? ` · ${capPoints} pts` : ""}`]].map(([l, v]) => (
            <GlassCard key={l} sx={{ p: 1.5 }}><Typography sx={labelSx}>{l}</Typography><Typography sx={{ fontWeight: 700, fontSize: "1.2rem" }}>{v}</Typography></GlassCard>
          ))}
        </Box>
      )}
      {(overHours || overPoints) && (
        <Alert severity="warning" icon={<AlertTriangle size={18} />} sx={{ mb: 2 }}>
          Sprint is over capacity{overHours ? ` — planned ${plan.committed.hours}h vs ${capHours}h` : ""}{overPoints ? ` — planned ${plan.committed.points} pts vs ${capPoints} pts` : ""}.{" "}
          {enforce ? "This board requires the sprint to fit capacity before it can start." : "You can still start it (the board does not enforce capacity)."}
        </Alert>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2.5 }}>
        <GlassCard sx={{ overflow: "hidden" }}>
          <CardHeader title={`Backlog (${pool?.total ?? 0})`} subtitle="Add issues to the sprint"
            action={<TextField size="small" placeholder="Search" value={text} onChange={(e) => setText(e.target.value)} sx={{ ...fieldSx, width: 160 }} />} />
          <Box sx={{ maxHeight: 460, overflowY: "auto" }}>
            {(pool?.issues || []).map((i) => (
              <IssueLine key={i._id} issue={i} unit={unit} action={editable && <Tooltip title="Add to sprint"><IconButton size="small" onClick={() => add([i._id], sprintId)}><Plus size={14} /></IconButton></Tooltip>} />
            ))}
            {!pool?.issues?.length && <Typography sx={{ p: 2, fontSize: TYPE.small, color: T.inkMuted }}>The backlog is empty.</Typography>}
          </Box>
        </GlassCard>
        <GlassCard sx={{ overflow: "hidden" }}>
          <CardHeader title={`${sprint?.name || "Sprint"} (${inSprint?.total ?? 0})`} subtitle={`${inSprint?.estimate ?? 0} ${unit} selected`} />
          <Box sx={{ maxHeight: 460, overflowY: "auto" }}>
            {(inSprint?.issues || []).map((i) => (
              <IssueLine key={i._id} issue={i} unit={unit} action={editable && <Tooltip title="Back to backlog"><IconButton size="small" onClick={() => add([i._id], null)}><Minus size={14} /></IconButton></Tooltip>} />
            ))}
            {!inSprint?.issues?.length && <Typography sx={{ p: 2, fontSize: TYPE.small, color: T.inkMuted }}>No issues selected yet.</Typography>}
          </Box>
        </GlassCard>
      </Box>
      {plan && (
        <GlassCard sx={{ mt: 2.5 }}>
          <CardHeader title="Team capacity" subtitle="Hours come from each member's hours/day × sprint working days unless set here." action={editable && <Button size="small" variant="contained" sx={primaryButtonSx} onClick={saveCap}>Save capacity</Button>} />
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead><TableRow>{["Member", "Issues", "Points", "Planned (h)", "Capacity (h)", "Capacity (pts)", "Remaining (h)"].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {plan.people.map((p) => {
                  const c = cap[String(p.memberId)] || {};
                  const remaining = Math.round(((Number(c.hours) || 0) - p.committedHours) * 10) / 10;
                  return (
                    <TableRow key={String(p.memberId)}>
                      <TableCell><Stack direction="row" spacing={1} alignItems="center"><PersonAvatar name={p.name} size={24} /><Typography sx={{ fontSize: TYPE.small }}>{p.name}</Typography></Stack></TableCell>
                      <TableCell>{p.issues}</TableCell>
                      <TableCell>{p.points}</TableCell>
                      <TableCell>{p.committedHours}</TableCell>
                      <TableCell>{editable ? <TextField size="small" type="number" value={c.hours ?? 0} onChange={(e) => setCap({ ...cap, [String(p.memberId)]: { ...c, hours: e.target.value } })} sx={{ ...fieldSx, width: 90 }} /> : c.hours}</TableCell>
                      <TableCell>{editable ? <TextField size="small" type="number" value={c.points ?? 0} onChange={(e) => setCap({ ...cap, [String(p.memberId)]: { ...c, points: e.target.value } })} sx={{ ...fieldSx, width: 90 }} /> : c.points}</TableCell>
                      <TableCell sx={{ color: remaining < 0 ? "#B42318" : T.ink, fontWeight: remaining < 0 ? 700 : 400 }}>{remaining}{remaining < 0 && " ⚠"}</TableCell>
                    </TableRow>
                  );
                })}
                {plan.unassigned.issues > 0 && <TableRow><TableCell sx={{ color: T.inkMuted }}>Unassigned</TableCell><TableCell>{plan.unassigned.issues}</TableCell><TableCell>{plan.unassigned.points}</TableCell><TableCell colSpan={4} /></TableRow>}
              </TableBody>
            </Table>
          </Box>
        </GlassCard>
      )}
      <SprintFormDialog state={starting ? { mode: "start", sprint: starting } : null} board={board} project={project} onClose={() => setStarting(null)} onDone={() => { setStarting(null); load(); reloadBoard(); }} />
    </Box>
  );
};

export default SprintPlanning;
